// src/services/api.js
import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Token helpers are no longer needed for headers, cookies are handled by browser

const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {},
});

api.interceptors.request.use(
    (config) => {
        // Tokens are now sent automatically via HttpOnly cookies
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Предотвращаем бесконечный цикл при 401 ответе от самого эндпоинта refresh
        if (originalRequest.url?.includes('/api/token/refresh/')) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Refresh request will automatically include the refresh_token cookie
                await axios.post(`${API_BASE_URL}/api/token/refresh/`, {}, { withCredentials: true });

                // After successful refresh, the access_token cookie is updated
                return api(originalRequest);
            } catch (refreshError) {
                console.error('Refresh token expired or invalid', refreshError);

                const wasLoggedIn = !!localStorage.getItem('user');
                localStorage.removeItem('user');
                // Force backend to clear HttpOnly cookies
                axios.post(`${API_BASE_URL}/api/auth/logout/`, {}, { withCredentials: true }).catch(() => { });

                // Перенаправляем на login только если пользователь до этого был авторизован
                if (wasLoggedIn && window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/api/token/', credentials),
    logout: () => {
        localStorage.removeItem('user');
        return api.post('/api/auth/logout/');
    },
    refreshToken: () => api.post('/api/token/refresh/', {}),
    // Остальные функции остаются без изменений, но используют JWT через интерцептор
    register: (userData) => api.post('/api/auth/register/', userData),
    getProfile: () => api.get('/api/users/me/'),
    checkAuth: () => api.get('/api/auth/check/'),
    changePassword: (data) => api.post('/api/auth/change-password/', data),
    checkUsername: (username) => api.get(`/api/auth/check-username/?username=${username}`),
    checkEmail: (email) => api.get(`/api/auth/check-email/?email=${email}`),
    verifyEmailCode: (email, code) => api.post('/api/auth/verify-email-code/', { email, code }),
    confirmEmail: (uid, token) => api.get(`/api/auth/confirm-email/${uid}/${token}/`),
    resendConfirmation: (email) => api.post('/api/auth/resend-confirmation/', { email }),

    // Password reset
    passwordReset: (email) => api.post('/api/auth/password-reset/', { email }),
    passwordResetConfirm: (uid, token, data) => api.post(`/api/auth/password-reset-confirm/${uid}/${token}/`, data),
};

// Users API
const usersAPI = {
    getUsers: (params = {}) => api.get('/api/users/', { params }),
    createUser: (data) => api.post('/api/users/', data),
    updateUser: (id, data) => api.patch(`/api/users/${id}/`, data),
    deleteUser: (id) => api.delete(`/api/users/${id}/`),

    // User actions
    deactivateUser: (id, data) => api.post(`/api/users/${id}/deactivate/`, data),
    activateUser: (id, data) => api.post(`/api/users/${id}/activate/`, data),
    updateProfile: (data) => api.patch('/api/users/my-profile/', data),
    me: () => api.get('/api/users/me/'),
    getUser: (identifier) => api.get(`/api/users/by-slug/${identifier}/`),
    getProfile: (identifier) => {
        if (!identifier) return api.get('/api/users/me/');
        return api.get(`/api/users/by-slug/${identifier}/`);
    },
    getNotifications: () => api.get('/api/users/notifications/'),
    approveUnblock: (id) => api.post(`/api/users/${id}/approve_unblock/`),
    rejectUnblock: (id) => api.post(`/api/users/${id}/reject_unblock/`),
    downloadResume: (slug) => api.get(`/api/users/${slug}/download-resume/`, { responseType: 'blob' }),
};

// Projects API
const projectsAPI = {
    getProjects: (params = {}) => api.get('/api/projects/', { params }),
    getProject: (slug) => api.get(`/api/projects/${slug}/`),
    createProject: (data) => api.post('/api/projects/', data),
    updateProject: (slug, data) => api.patch(`/api/projects/${slug}/`, data),
    deleteProject: (slug) => api.delete(`/api/projects/${slug}/`),

    // Project actions
    completeProject: (slug) => api.post(`/api/projects/${slug}/complete/`),
    reopenProject: (slug) => api.post(`/api/projects/${slug}/reopen/`),
    addTag: (slug, tagName) => api.post(`/api/projects/${slug}/add_tag/`, { tag_name: tagName }),
    removeTag: (slug, tagId) => api.delete(`/api/projects/${slug}/remove_tag/`, { data: { tag_id: tagId } }),
    approveProject: (slug) => api.post(`/api/projects/${slug}/approve/`),
    startTeamBuilding: (slug) => api.post(`/api/projects/${slug}/start_team_building/`),
    addInvestor: (slug, investorData) => api.post(`/api/projects/${slug}/add_investor/`, investorData),
    getProjectInvestors: (slug) => api.get(`/api/projects/${slug}/investors/`),
    transferOwnership: (slug, newOwnerId) => api.post(`/api/projects/${slug}/transfer_ownership/`, { new_owner_id: newOwnerId }),
};

// Tracks API
const tracksAPI = {
    getTracks: (params = {}) => api.get('/api/tracks/', { params }),
    getTrack: (slug) => api.get(`/api/tracks/${slug}/`),
    createTrack: (data) => api.post('/api/tracks/', data),
    updateTrack: (slug, data) => api.patch(`/api/tracks/${slug}/`, data),
    deleteTrack: (slug) => api.delete(`/api/tracks/${slug}/`),

    // Track actions
    archiveTrack: (slug) => api.post(`/api/tracks/${slug}/archive/`),
    activateTrack: (slug) => api.post(`/api/tracks/${slug}/activate/`),
    applyToTrack: (id) => api.post(`/api/tracks/${id}/apply/`),
    applyAsExpert: (slug) => api.post(`/api/tracks/${slug}/apply_as_expert/`),
    applyAsMentor: (slug) => api.post(`/api/tracks/${slug}/apply_as_mentor/`),
    inviteUserToTrack: (slug, userId, role) => api.post(`/api/tracks/${slug}/invite_user/`, { user_id: userId, role }),
    removeUserFromTrack: (slug, userId) => api.delete(`/api/tracks/${slug}/remove_user/`, { data: { user_id: userId } }),
    reviewApplication: (slug, data) => api.post(`/api/tracks/${slug}/review_application/`, data),
    getTrackParticipants: (slug) => api.get(`/api/tracks/${slug}/participants/`),
    addMentor: (slug, mentorData) => api.post(`/api/tracks/${slug}/add_mentor/`, mentorData),
    removeMentor: (slug, mentorId) => api.delete(`/api/tracks/${slug}/remove_mentor/`, { data: { mentor_id: mentorId } }),
    addExpert: (slug, expertData) => api.post(`/api/tracks/${slug}/add_expert/`, expertData),
    removeExpert: (slug, expertId) => api.delete(`/api/tracks/${slug}/remove_expert/`, { data: { expert_id: expertId } }),

    // Track stages
    addTrackStage: (slug, data) => api.post(`/api/tracks/${slug}/add_track_stage/`, data),
    updateTrackStage: (slug, data) => api.patch(`/api/tracks/${slug}/update_track_stage/`, data),
    deleteTrackStage: (slug, stageId) => api.delete(`/api/tracks/${slug}/remove_track_stage/`, { params: { stage_id: stageId } }),

    // Membership
    leave: (slug) => api.post(`/api/tracks/${slug}/leave/`),
    requestMentorRole: (slug) => api.post(`/api/tracks/${slug}/request_mentor_role/`),

    // Freezing
    freezeTrack: (slug) => api.post(`/api/tracks/${slug}/freeze/`),
    unfreezeTrack: (slug) => api.post(`/api/tracks/${slug}/unfreeze/`),

    // Hackathon mentor assignment
    assignProjectMentor: (slug, projectId, mentorId) => api.post(`/api/tracks/${slug}/assign_project_mentor/`, { project_id: projectId, mentor_id: mentorId }),

    // New organization action — applyAsExpert уже определён выше (строка 137)
};

// Teams API
const teamsAPI = {
    getTeams: (params = {}) => api.get('/api/teams/', { params }),
    getTeam: (id) => api.get(`/api/teams/${id}/`),
    createTeam: (data) => api.post('/api/teams/', data),
    updateTeam: (id, data) => api.patch(`/api/teams/${id}/`, data),
    deleteTeam: (id) => api.delete(`/api/teams/${id}/`),

    // Team actions
    disbandTeam: (id) => api.post(`/api/teams/${id}/disband/`),
    addMember: (id, memberId) => api.post(`/api/teams/${id}/add_member/`, { member_id: memberId }),
    removeMember: (id, memberId) => api.delete(`/api/teams/${id}/remove_member/`, { data: { member_id: memberId } }),
    applyToTeam: (id, data) => api.post(`/api/teams/${id}/apply/`, data),
    getTeamApplications: (id) => api.get(`/api/teams/${id}/applications/`),
    reviewTeamApplication: (id, data) => api.post(`/api/teams/${id}/review_application/`, data),
    setCaptain: (id, memberId) => api.post(`/api/teams/${id}/set_captain/`, { member_id: memberId }),
};




// Documents API
const documentsAPI = {
    getProjectDocuments: (params = {}) => api.get('/api/project-documents/', { params }),
    getProjectDocument: (id) => api.get(`/api/project-documents/${id}/`),
    createProjectDocument: (data) => api.post('/api/project-documents/', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateProjectDocument: (id, data) => {
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        return api.patch(`/api/project-documents/${id}/`, data, isFormData ? {
            headers: { 'Content-Type': 'multipart/form-data' }
        } : {});
    },
    deleteProjectDocument: (id) => api.delete(`/api/project-documents/${id}/`),
};

// Team Members API
const teamMembersAPI = {
    getTeamMembers: (params = {}) => api.get('/api/team-members/', { params }),
    getTeamMember: (id) => api.get(`/api/team-members/${id}/`),
    createTeamMember: (data) => api.post('/api/team-members/', data),
    updateTeamMember: (id, data) => api.patch(`/api/team-members/${id}/`, data),
    deleteTeamMember: (id) => api.delete(`/api/team-members/${id}/`),
};


// Vacancies API
const vacanciesAPI = {
    getVacancies: (params = {}) => api.get('/api/vacancies/', { params }),
    getVacancy: (identifier) => api.get(`/api/vacancies/${identifier}/`),
    createVacancy: (data) => api.post('/api/vacancies/', data),
    updateVacancy: (identifier, data) => api.patch(`/api/vacancies/${identifier}/`, data),
    deleteVacancy: (identifier) => api.delete(`/api/vacancies/${identifier}/`),
};

// Track Participations API
const trackParticipationsAPI = {
    getTrackParticipations: (params = {}) => api.get('/api/track-participations/', { params }),
    getTrackParticipation: (id) => api.get(`/api/track-participations/${id}/`),
    createTrackParticipation: (data) => api.post('/api/track-participations/', data),
    updateTrackParticipation: (id, data) => api.patch(`/api/track-participations/${id}/`, data),
    deleteTrackParticipation: (id) => api.delete(`/api/track-participations/${id}/`),

    // Track participation actions
    acceptInvitation: (id) => api.post(`/api/track-participations/${id}/accept_invitation/`),
    rejectInvitation: (id) => api.post(`/api/track-participations/${id}/reject_invitation/`),
    cancelInvitation: (id) => api.post(`/api/track-participations/${id}/cancel_invitation/`),
    approve: (id) => api.post(`/api/track-participations/${id}/approve/`),
    reject: (id) => api.post(`/api/track-participations/${id}/reject/`),
    withdrawApplication: (id) => api.post(`/api/track-participations/${id}/withdraw_application/`),
};

// Project Participations API
const projectParticipationsAPI = {
    getProjectParticipations: (params = {}) => api.get('/api/project-participations/', { params }),
    getProjectParticipation: (id) => api.get(`/api/project-participations/${id}/`),
    createProjectParticipation: (data) => api.post('/api/project-participations/', data),
    updateProjectParticipation: (id, data) => api.patch(`/api/project-participations/${id}/`, data),
    deleteProjectParticipation: (id) => api.delete(`/api/project-participations/${id}/`),

    // Participation actions
    leave: (data) => api.post('/api/project-participations/leave/', data),
    approve: (id) => api.post(`/api/project-participations/${id}/approve/`),
    reject: (id) => api.post(`/api/project-participations/${id}/reject/`),
    excludeUser: (projectId, userId) => api.post('/api/project-participations/remove_user/', { project_id: projectId, user_id: userId }),
    inviteUser: (data) => api.post('/api/project-participations/invite_user/', data),
    reviewApplication: (data) => api.post('/api/project-participations/review_application/', data),
    acceptInvitation: (id) => api.post(`/api/project-participations/${id}/accept_invitation/`),
    rejectInvitation: (id) => api.post(`/api/project-participations/${id}/reject_invitation/`),
    withdrawApplication: (id) => api.post(`/api/project-participations/${id}/withdraw_application/`),
};

// Project Stages API
const projectStagesAPI = {
    getStages: (projectId) => api.get('/api/project-stages/', { params: { project_id: projectId } }),
    createStage: (data) => api.post('/api/project-stages/', data),
    updateStage: (id, data) => api.patch(`/api/project-stages/${id}/`, data),
    deleteStage: (id) => api.delete(`/api/project-stages/${id}/`),
    setInProgress: (id) => api.post(`/api/project-stages/${id}/set_in_progress/`),
    resetToPending: (id) => api.post(`/api/project-stages/${id}/reset_to_pending/`),
    togglePublic: (id, data = {}) => api.post(`/api/project-stages/${id}/toggle_public/`, data),
};

// Stage Completion Requests API
const stageCompletionRequestsAPI = {
    getRequests: (params = {}) => api.get('/api/stage-completion-requests/', { params }),
    getRequest: (identifier) => api.get(`/api/stage-completion-requests/${identifier}/`),
    createRequest: (formData) => api.post('/api/stage-completion-requests/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    approveRequest: (identifier) => api.post(`/api/stage-completion-requests/${identifier}/approve/`),
    rejectRequest: (identifier, data) => api.post(`/api/stage-completion-requests/${identifier}/reject/`, data),
    resubmitRequest: (identifier) => api.post(`/api/stage-completion-requests/${identifier}/resubmit/`),
    resetToPending: (identifier) => api.post(`/api/stage-completion-requests/${identifier}/reset_to_pending/`),
    updateRequest: (identifier, formData) => api.patch(`/api/stage-completion-requests/${identifier}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    withdrawRequest: (identifier) => api.post(`/api/stage-completion-requests/${identifier}/withdraw/`),
};

// Stage Verification Comments API
const stageVerificationCommentsAPI = {
    getComments: (requestId) => api.get('/api/stage-verification-comments/', { params: { request_id: requestId } }),
    createComment: (data) => api.post('/api/stage-verification-comments/', data),
};

// Project Track Applications API
const projectTrackApplicationsAPI = {
    getApplications: (params = {}) => api.get('/api/project-track-applications/', { params }),
    apply: (data) => api.post('/api/project-track-applications/', data),
    approve: (id) => api.post(`/api/project-track-applications/${id}/approve/`),
    reject: (id) => api.post(`/api/project-track-applications/${id}/reject/`),
    withdraw: (id) => api.post(`/api/project-track-applications/${id}/withdraw/`),
};

// News API
const newsAPI = {
    getNews: (params) => api.get('/api/news/', { params }),
    getNewsItem: (slug) => api.get(`/api/news/${slug}/`),
    createNews: (data) => api.post('/api/news/', data),
    updateNews: (slug, data) => api.patch(`/api/news/${slug}/`, data),
    deleteNews: (slug) => api.delete(`/api/news/${slug}/`),
};

// Cases API
const casesAPI = {
    getCases: (params = {}) => api.get('/api/cases/', { params }),
    getCase: (slug) => api.get(`/api/cases/${slug}/`),
    createCase: (data) => api.post('/api/cases/', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateCase: (slug, data) => api.patch(`/api/cases/${slug}/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteCase: (slug) => api.delete(`/api/cases/${slug}/`),
    
    // Case actions
    createProjectFromCase: (slug) => api.post(`/api/cases/${slug}/create-project/`),
    applyForCase: (slug, data) => api.post(`/api/cases/${slug}/apply/`, data),
    getMyApplication: (slug) => api.get(`/api/cases/${slug}/my-application/`),
    withdrawApplication: (slug) => api.post(`/api/cases/${slug}/withdraw-application/`),
    getApplications: (slug) => api.get(`/api/cases/${slug}/applications/`),
    reviewApplication: (slug, data) => api.post(`/api/cases/${slug}/review-application/`, data),
    announceWinners: (slug, projectIds) => api.post(`/api/cases/${slug}/announce-winners/`, { project_ids: projectIds }),
    updateStatus: (slug, status) => api.patch(`/api/cases/${slug}/update-status/`, { status }),
    downloadDetails: (slug) => `${API_BASE_URL}/api/cases/${slug}/download-detailed-description/`,
};

// Unblock Requests API
const unblockRequestsAPI = {
    getRequests: (params = {}) => api.get('/api/unblock-requests/', { params }),
    getRequest: (id) => api.get(`/api/unblock-requests/${id}/`),
    approveRequest: (id) => api.post(`/api/unblock-requests/${id}/approve/`),
    rejectRequest: (id) => api.post(`/api/unblock-requests/${id}/reject/`),
};

// Role Applications API
const roleApplicationsAPI = {
    getApplications: (params = {}) => api.get('/api/role-applications/', { params }),
    getApplication: (id) => api.get(`/api/role-applications/${id}/`),
    createApplication: (data) => api.post('/api/role-applications/', data),
    approveApplication: (id) => api.post(`/api/role-applications/${id}/approve/`),
    rejectApplication: (id) => api.post(`/api/role-applications/${id}/reject/`),
    withdrawApplication: (id) => api.post(`/api/role-applications/${id}/withdraw/`),
};

// Skills API
const skillsAPI = {
    getSkills: (params = {}) => api.get('/api/skills/', { params }),
    getSkillSuggestions: (params = {}) => api.get('/api/skills/suggestions/', { params }),
    getSkillTree: () => api.get('/api/skills/tree/'),
};

// Utility API
const utilityAPI = {
    getCSRFToken: () => api.get('/csrf/'),
    debugTemplates: () => api.get('/api/debug/templates/'),
    checkImports: () => api.get('/api/check-imports/'),
};

// Notifications API
const notificationsAPI = {
    getNotifications: (params = {}) => api.get('/api/notifications/', { params }),
    markAsRead: (id) => api.patch(`/api/notifications/${id}/mark_as_read/`),
    markAllAsRead: () => api.post('/api/notifications/mark_all_as_read/'),
};

// Admin API (placeholder if not defined)
const adminAPI = {
    getStats: () => api.get('/api/admin-dashboard/stats/'),
    getLogs: () => api.get('/api/admin-dashboard/logs/'),
    getModerationList: (params) => api.get('/api/admin-dashboard/moderation-list/', { params }),
    deleteUser: (id) => api.delete(`/api/admin-dashboard/${id}/delete-user/`),
    deleteProject: (id) => api.delete(`/api/admin-dashboard/${id}/delete-project/`),
    deleteTrack: (id) => api.delete(`/api/admin-dashboard/${id}/delete-track/`),
    deleteNews: (id) => api.delete(`/api/admin-dashboard/${id}/delete-news/`),
    backupDB: () => api.get('/api/admin-dashboard/backup-db/', { responseType: 'blob' }),
    backupMedia: () => api.get('/api/admin-dashboard/backup-media/', { responseType: 'blob' }),
    getScheduledBackups: () => api.get('/api/admin-dashboard/backups-list/'),
    downloadScheduledBackup: (id) => api.get(`/api/admin-dashboard/download-backup/${id}/`, { responseType: 'blob' }),
};

// Combined API object for easy imports
export const API = {
    admin: adminAPI,
    auth: authAPI,
    users: usersAPI,
    projects: projectsAPI,
    tracks: tracksAPI,
    teams: teamsAPI,
    notifications: notificationsAPI,

    documents: documentsAPI,
    teamMembers: teamMembersAPI,
    trackParticipations: trackParticipationsAPI,
    projectParticipations: projectParticipationsAPI,
    vacancies: vacanciesAPI,
    utility: utilityAPI,
    news: newsAPI,
    roleApplications: roleApplicationsAPI,
    unblockRequests: unblockRequestsAPI,
    projectStages: projectStagesAPI,
    stageCompletionRequests: stageCompletionRequestsAPI,
    stageVerificationComments: stageVerificationCommentsAPI,
    projectTrackApplications: projectTrackApplicationsAPI,
    cases: casesAPI,
    skills: skillsAPI,
};

export default api;